import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Star } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { fadeUp, displaySection, kicker } from './shared';

const TRUST_AVATARS = [
  { src: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=facearea&facepad=2.5&w=200&h=200&q=80', fb: 'MS' },
  { src: 'https://images.unsplash.com/photo-1603985156220-90fc062e0521?auto=format&fit=facearea&facepad=2.5&w=200&h=200&q=80', fb: 'DM' },
  { src: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?auto=format&fit=facearea&facepad=2.5&w=200&h=200&q=80', fb: 'AR' },
  { src: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=facearea&facepad=2.5&w=200&h=200&q=80', fb: 'PR' },
  { src: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=facearea&facepad=2.5&w=200&h=200&q=80', fb: 'DL' },
];

export const FinalCTA = ({ onPrimaryCta }) => (
  <section className="relative border-t border-border/60" id="final-cta" aria-label="Get started">
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
      <Card
        className="relative rounded-[36px] border border-border/70 bg-card overflow-hidden"
        data-testid="final-cta-section"
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(700px circle at 80% 10%, hsl(212 92% 70% / 0.18), transparent 55%), radial-gradient(600px circle at 12% 90%, hsl(205 85% 60% / 0.12), transparent 60%)',
          }}
        />
        <CardContent className="relative p-10 sm:p-16 lg:p-20">
          {/* Joined-by trust strip */}
          <motion.div {...fadeUp} className="flex flex-wrap items-center gap-4">
            <div className="flex -space-x-2.5" aria-hidden="true">
              {TRUST_AVATARS.map((a, i) => (
                <Avatar key={i} className="h-9 w-9 ring-2 ring-card shadow-sm">
                  <AvatarImage src={a.src} alt="" className="object-cover" loading="lazy" />
                  <AvatarFallback className="bg-secondary text-foreground text-[10px] font-medium">{a.fb}</AvatarFallback>
                </Avatar>
              ))}
            </div>
            <div>
              <div className="flex items-center gap-1.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={12} className="text-primary fill-primary" aria-hidden="true" />
                ))}
                <span className="text-[12px] font-medium text-foreground/85 ml-1">4.9 / 5</span>
              </div>
              <p className="mt-1 text-[12px] sm:text-[13px] text-muted-foreground">
                Joined by <strong className="text-foreground">2,400+</strong> professionals building a quieter LinkedIn presence.
              </p>
            </div>
          </motion.div>

          <motion.div {...fadeUp} className="mt-10 max-w-3xl">
            <p className={kicker}>Your next opportunity</p>
            <h2 className={`${displaySection} mt-5 text-4xl sm:text-5xl lg:text-7xl`}>
              They&rsquo;ll check your LinkedIn before your resume, website, or proposal.{' '}
              <span className="text-primary">Make sure they find the right version of you.</span>
            </h2>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground">
              Upload your LinkedIn PDF. 10 seconds to see what to fix first &mdash; before you decide on a plan.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Button
                onClick={() => document.getElementById('calculator')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="final-cta-button"
                size="lg"
                className="h-12 px-6 rounded-full text-[15px] shadow-sm"
              >
                Check my LinkedIn score
                <ArrowRight size={15} className="ml-1.5" aria-hidden="true" />
              </Button>
              <Button
                variant="ghost"
                size="lg"
                onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="final-cta-see-pricing"
                className="h-12 px-5 rounded-full text-[15px] text-foreground hover:bg-secondary"
              >
                See pricing
                <ArrowRight size={15} className="ml-1" aria-hidden="true" />
              </Button>
            </div>
          </motion.div>

          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.12 }}
            className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl"
          >
            {[
              ['10 sec', 'Score preview time'],
              ['\u20b9199', 'Profile Success plan'],
              ['100%', 'Editable outputs'],
              ['GDPR', 'UK PECR compliant'],
            ].map(([k, l]) => (
              <div key={l} className="rounded-2xl border border-border/70 bg-background/70 backdrop-blur p-5">
                <p className="font-display font-semibold text-4xl tracking-[-0.05em] tabular-nums">{k}</p>
                <p className="mt-1.5 text-[11px] font-mono-edge tracking-[0.2em] uppercase text-muted-foreground">{l}</p>
              </div>
            ))}
          </motion.div>
        </CardContent>
      </Card>
    </div>
  </section>
);
